import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Rating,
  IconButton,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardMedia,
  Pagination,
  Select,
  MenuItem,
  Modal,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  GridView,
  List as ListIcon,
} from "@mui/icons-material";
import Notes from "./header";
import FooterManual from "./footerManual";
import InquiryModal from "./inquiryModal";
import { useNavigate } from "react-router-dom";
import {
  addToWishlist,
  fetchCart,
  fetchWishlist,
} from "./store";
import { useSnackbar } from "notistack";
import { styled } from "@mui/material/styles";
import axios from 'axios';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
  overflow: "hidden",
  marginBottom: "16px",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    width: `calc(100% + ${theme.spacing(2)})`,
    "& .MuiTable-root": { minWidth: "650px" },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  [theme.breakpoints.down("sm")]: {
    "& > .MuiTableCell-root": { padding: theme.spacing(1) },
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    fontSize: "0.75rem",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiCardMedia-root": { objectFit: "contain" },
    "& .MuiCardContent-root": { padding: "20px 8px" },
    "& .MuiButton-root": {
      fontSize: "0.7rem",
      padding: "4px 8px",
      minWidth: "unset",
    },
    "& .MuiTypography-h6": { fontSize: "0.9rem" },
    "& .MuiTypography-body2": { fontSize: "0.75rem" },
    "& .MuiChip-root": {
      height: "20px",
      "& .MuiChip-label": { fontSize: "0.65rem" },
    },
  },
}));

const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
}));

// Utility function to remove duplicate brand name from model name
const cleanModelName = (brandName, modelName) => {
  if (!brandName || !modelName) return modelName || '';
  
  const brandLower = brandName.toLowerCase().trim();
  const modelLower = modelName.toLowerCase().trim();
  
  if (modelLower.startsWith(brandLower)) {
    return modelName.substring(brandName.length).trim();
  }
  
  return modelName;
};

// Car Card Grid Component
const CarListingCardGrid = ({
  car,
  onCarClick,
  imageUrl,
  onInquiryClick
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const isFavorite = wishlistItems.some((item) => item.modelId === car.carModelId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  const handleConfirmRemove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${car.carModelId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        enqueueSnackbar("Item removed from wishlist successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to remove item from wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error removing item from wishlist", {
        variant: "error",
      });
      console.error("Error removing from wishlist:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      if (isFavorite) {
        setDialogType("remove");
        setOpenDialog(true);
        return;
      }
      const result = await dispatch(addToWishlist(car.carModelId));
      if (result.success) {
        enqueueSnackbar("Added to wishlist successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Failed to add to wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to wishlist", { variant: "error" });
      console.error("Error adding to wishlist:", error);
    }
  };

  return (
    <StyledCard
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: isMobile ? "180px" : "250px",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        <CardMedia
          component="img"
          image={imageUrl || "carousel/carslider.png"}
          alt={`${car.carBrandName} ${cleanModelName(car.carBrandName, car.carModelName)}`}
          sx={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
            objectFit: isMobile ? "contain" : "cover",
            width: "100%",
            height: "100%",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "carousel/carslider.png";
          }}
        />

        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: isMobile ? "4px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
          onClick={handleFavoriteClick}
        >
          {isFavorite ? (
            <Favorite
              sx={{
                color: "red",
                fontSize: isMobile ? "1rem" : "1.25rem",
                animation: isFavorite ? "pulse 0.5s ease" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          ) : (
            <FavoriteBorder
              sx={{
                fontSize: isMobile ? "1rem" : "1.25rem",
                color: theme.palette.grey[600],
              }}
            />
          )}
        </IconButton>
      </Box>

      <Box
        sx={{
          marginTop: "-15px",
          marginLeft: "15px",
          bottom: 8,
          left: 8,
          display: "flex",
          gap: 0.5,
          flexWrap: "wrap",
          zIndex: 1111
        }}
      >
        {car.ourPick && (
          <Chip
            label="Our Pick"
            size="small"
            sx={{
              fontSize: isMobile ? "0.55rem" : "0.65rem",
              height: isMobile ? "16px" : "26px",
              color: "#dd006e",
              backgroundColor: "#ffbddf",
              fontWeight: "bold",
              border: "1px solid #FF69B4",
              "&:hover": { backgroundColor: "white" },
            }}
          />
        )}
        {car.carGrade && (
          <Chip
            label={`Grade ${car.carGrade || "A"}`}
            size="small"
            sx={{
              fontSize: isMobile ? "0.55rem" : "0.65rem",
              height: isMobile ? "16px" : "26px",
              color: "#009966",
              backgroundColor: "#9fffe7",
              fontWeight: "bold",
              border: "1px solid #00FFAA",
              "&:hover": { backgroundColor: "white" },
            }}
          />
        )}
        {car.specialDeal && (
          <Chip
            label="Special Offer"
            size="small"
            sx={{
              fontSize: isMobile ? "0.55rem" : "0.65rem",
              height: isMobile ? "16px" : "26px",
              backgroundColor: "#d09fff",
              color: "#6c2aab",
              fontWeight: "bold",
              border: "1px solid #6c2aab",
              "&:hover": { backgroundColor: "white" },
            }}
          />
        )}
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          p: isMobile ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => onCarClick(car.carModelId)}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              mb: 0.5,
              color: theme.palette.text.primary,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,
            "& > div": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            },
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Ref #:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carRefNumber || "N/A"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Year:
            </Typography>
            {car.year && <span>{car.year}</span>}
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Engine:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carEngineCC || "N/A"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex" }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Fuel:
            </Typography>
            {car.carFuel && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                  color: theme.palette.text.primary,
                }}
              >
                {car.carFuel}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "8px" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "20px",
            }}
          >
            Price:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "20px",
              fontWeight: 700,
              color: "#28a745",
            }}
          >
            {car.carPrice
              ? `$${car.carPrice.toLocaleString()}`
              : "Price on request"}
          </Typography>
        </Box>
      </CardContent>

      <Box
        sx={{
          p: isMobile ? 1 : 1.5,
          pt: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          size="medium"
          sx={{
            bgcolor: "#28a745",
            color: "white",
            border: "1px solid #28a745",
            borderRadius: "50px",
            fontSize: "0.75rem",
            fontWeight: "600",
            padding: "18px 18px",
            minWidth: "150px",
            height: "38px",
            width: "100%",
            transition: "all 0.3s ease",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "#28a745",
              transform: "translateX(-100%)",
              transition: "transform 0.3s ease",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              boxShadow: "0 3px 6px rgba(40,167,69,0.3)",
              "&::before": {
                transform: "translateX(0)",
              },
            },
          }}
          onClick={(e) => onInquiryClick(e, car.carModelId)}
        >
          GET QUOTE
        </Button>
      </Box>

      <Modal
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setDialogType("");
        }}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              id="dialog-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              Remove from Wishlist
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 3 }}>
            <Typography id="dialog-description">
              Are you sure you want to remove this item from your wishlist?
            </Typography>
          </Box>

          <Box sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              onClick={() => {
                setOpenDialog(false);
                setDialogType("");
              }}
              sx={{
                border: "1px solid rgb(54 54 54)",
                color: "#282828",
                padding: "0px 18px"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyledCard>
  );
};

// Car Listing Table Component - UPDATED TO MATCH GLOBAL SEARCH
const CarListingTable = ({
  cars,
  onCarClick,
  imageUrl,
  onInquiryClick
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const visibleColumns = isMobile ? 10 : isTablet ? 10 : 11;
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(null);

  const handleFavoriteClick = async (e, carModelId, isFavorite) => {
    e.stopPropagation();
    setSelectedModelId(carModelId);

    if (isFavorite) {
      setOpenDialog(true);
    } else {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }
        const result = await dispatch(addToWishlist(carModelId));
        if (result.success) {
          enqueueSnackbar("Added to wishlist successfully", {
            variant: "success",
          });
        } else if (result.alreadyInWishlist) {
          enqueueSnackbar("This item is already in your wishlist", {
            variant: "info",
          });
        } else {
          enqueueSnackbar(result.message || "Failed to add to wishlist", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Error adding to wishlist", { variant: "error" });
        console.error("Error adding to wishlist:", error);
      }
    }
  };

  const handleConfirmAction = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${selectedModelId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        enqueueSnackbar("Removed from wishlist successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to remove from wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error processing request", { variant: "error" });
      console.error("Error:", error);
    } finally {
      setOpenDialog(false);
      setSelectedModelId(null);
    }
  };

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell align="center">
              Drive This Beauty
            </StyledTableHeaderCell>
            {visibleColumns > 1 && (
              <StyledTableHeaderCell align="center">
                Reference Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 2 && (
              <StyledTableHeaderCell align="center">
                Chassis Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 3 && (
              <StyledTableHeaderCell align="center">
                Registration Year
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 4 && (
              <StyledTableHeaderCell align="center">
                Engine CC / Motor kW
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 5 && (
              <StyledTableHeaderCell align="center">
                Fuel Type
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 7 && (
              <StyledTableHeaderCell align="center">
                Body Color
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 8 && (
              <StyledTableHeaderCell align="center">
                Drive Type
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 9 && (
              <StyledTableHeaderCell align="center">
                Price
              </StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell align="center">
              Actions
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {cars.map((car) => {
            const isFavorite = wishlistItems.some(
              (item) => item.modelId === car.carModelId
            );
            const carImageUrl =
              imageUrl[car.carModelId] || "carousel/carslider.png";

            return (
              <StyledTableRow
                key={car.carModelId}
                onClick={() => onCarClick(car.carModelId)}
              >
                <TableCell
                  sx={{
                    px: { xs: 1, sm: 2 },
                    minWidth: isMobile ? "180px" : "250px",
                    maxWidth: isMobile ? "200px" : "300px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 1 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: isMobile ? 60 : 80,
                        height: isMobile ? 45 : 60,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={carImageUrl}
                        alt={`${car.carBrandName} ${cleanModelName(car.carBrandName, car.carModelName)}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          backgroundColor: "#f5f5f5",
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "carousel/carslider.png";
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) =>
                          handleFavoriteClick(e, car.carModelId, isFavorite)
                        }
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          padding: isMobile ? "2px" : "3px",
                          backgroundColor: "rgba(255,255,255,0.8)",
                        }}
                      >
                        {isFavorite ? (
                          <Favorite sx={{ fontSize: isMobile ? 10 : 14 }} />
                        ) : (
                          <FavoriteBorder
                            sx={{ fontSize: isMobile ? 10 : 14 }}
                          />
                        )}
                      </IconButton>
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                        }}
                      >
                        {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {visibleColumns > 1 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carRefNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 2 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carChassisNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 3 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.year || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 4 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/engine.svg"
                        alt="Engine"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carEngineCC ? `${car.carEngineCC}` : "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 5 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/MDI - gas-station.svg"
                        alt="Fuel"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carFuel || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 7 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 12 : 16,
                          height: isMobile ? 12 : 16,
                          borderRadius: "50%",
                          backgroundColor:
                            car.carColor?.toLowerCase() || "#ccc",
                          border: "1px solid #ddd",
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                      >
                        {car.carColor || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {visibleColumns > 8 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carDrive || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 9 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.75rem" : "0.95rem",
                        color: "#28a745",
                      }}
                    >
                      {car.carPrice
                        ? `$${car.carPrice.toLocaleString()}`
                        : "On Request"}
                    </Typography>
                  </TableCell>
                )}

                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.8,
                      position: "relative",
                      padding: "6px 0",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "#28a745",
                        color: "white",
                        border: "1px solid #28a745",
                        borderRadius: "16px",
                        fontSize: "0.65rem",
                        padding: "3px 8px",
                        minWidth: "70px",
                        maxWidth: "90px",
                        height: "28px",
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "#28a745",
                          transform: "translateX(-100%)",
                          transition: "transform 0.3s ease",
                          zIndex: -1,
                        },
                        "&:hover": {
                          color: "white",
                          boxShadow: "0 3px 6px rgba(40,167,69,0.3)",
                          "&::before": {
                            transform: "translateX(0)",
                          },
                        },
                      }}
                      onClick={(e) => onInquiryClick(e, car.carModelId)}
                    >
                      GET QUOTE
                    </Button>
                  </Box>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>

      <Modal
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedModelId(null);
        }}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              id="dialog-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              Remove from Wishlist
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 3 }}>
            <Typography id="dialog-description">
              Are you sure you want to remove this item from your wishlist?
            </Typography>
          </Box>

          <Box sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              onClick={() => {
                setOpenDialog(false);
                setSelectedModelId(null);
              }}
              sx={{
                border: "1px solid rgb(54 54 54)",
                color: "#282828",
                padding: "0px 18px"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyledTableContainer>
  );
};

// Main Component
const SeeAllAffordableCarModels = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [cars, setCars] = useState([]);
  const [imageUrl, setImageUrl] = useState({});
  const [viewMode, setViewMode] = useState("list");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // State for Inquiry Modal
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [inquiryProduct, setInquiryProduct] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  // Fetch Affordable Cars
  useEffect(() => {
    const fetchAffordableCars = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `https://car.amar.co.jp/api/master/affordable-car/get-all-affordable-car?page=${currentPage - 1}&size=${itemsPerPage}`,
          {
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en'
            }
          }
        );
        const data = await response.json();
        
        if (data.data && data.data.length > 0) {
          setCars(data.data);
          setTotalItems(data.totalItems || 0);
          setTotalPages(data.totalPages || 0);
        } else {
          setCars([]);
          setTotalItems(0);
          setTotalPages(0);
        }
      } catch (error) {
        console.error("Error fetching affordable cars:", error);
        enqueueSnackbar("Failed to load affordable cars", { variant: "error" });
        setCars([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAffordableCars();
    
    const token = localStorage.getItem("authToken");
    if (token) {
      dispatch(fetchWishlist());
      dispatch(fetchCart());
    }
  }, [currentPage, itemsPerPage, dispatch, enqueueSnackbar]);

  // Fetch car images
  const fetchCarImage = async (carModelId) => {
    try {
      for (let i = 1; i <= 5; i++) {
        const imageEndpoint = `https://car.amar.co.jp/api/image/car-exterior-images-${i}/get-image/${carModelId}`;
        
        try {
          const response = await fetch(imageEndpoint);
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              return URL.createObjectURL(blob);
            }
          }
        } catch (err) {
          continue;
        }
      }
      
      return `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`;
    } catch (error) {
      console.error("Error fetching car image:", error);
      return `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`;
    }
  };

  // Load images when car data changes
  useEffect(() => {
    if (cars && cars.length > 0) {
      const loadImages = async () => {
        const imagePromises = cars.map(async (car) => {
          if (!imageUrl[car.carModelId]) {
            const imageUrlData = await fetchCarImage(car.carModelId);
            return { carModelId: car.carModelId, imageUrl: imageUrlData };
          }
          return null;
        });

        const results = await Promise.all(imagePromises);
        const newImages = {};
        
        results.forEach((result) => {
          if (result && result.imageUrl) {
            newImages[result.carModelId] = result.imageUrl;
          }
        });

        if (Object.keys(newImages).length > 0) {
          setImageUrl((prev) => ({ ...prev, ...newImages }));
        }
      };

      loadImages();
    }
  }, [cars]);

  // Set default view to grid on mobile
  useEffect(() => {
    if (isMobile && viewMode === "list") {
      setViewMode("grid");
    }
  }, [isMobile]);

  // View mode handler
  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) setViewMode(newViewMode);
  };

  // Page change handler
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Items per page change handler
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  // Car click handler
  const handleCarClick = (modelId) => {
    navigate(`/product/${modelId}`);
  };

  // Inquiry Modal Functions
  const fetchProductData = async (carModelId) => {
    try {
      const response = await axios.get(
        `https://car.amar.co.jp/api/dashboard/product/get/Model/details/${carModelId}`
      );
      return response.data?.data || null;
    } catch (err) {
      console.error('Error fetching product data:', err);
      return null;
    }
  };

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateValidImageUrls = async (type, modelId, maxCount = 15) => {
    const baseUrl = `https://car.amar.co.jp/api/image/car-${type}-images-`;
    const urls = await Promise.allSettled(
      Array.from({ length: maxCount }, (_, i) =>
        checkImageExists(`${baseUrl}${i + 1}/get-image/${modelId}`)
      )
    );
    return urls
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
  };

  const handleInquiryClick = async (e, carModelId) => {
    e.stopPropagation();
    const productData = await fetchProductData(carModelId);
    if (productData) {
      setInquiryProduct(productData);
      const imageUrls = await generateValidImageUrls("exterior", carModelId);
      setModalImage(imageUrls[0] || 'carousel/carslider.png');
      setOpenInquiryModal(true);
    } else {
      enqueueSnackbar('Failed to load car details for inquiry.', { variant: 'error' });
    }
  };

  const handleInquiryClose = () => {
    setOpenInquiryModal(false);
    setInquiryProduct(null);
    setModalImage(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>
      <Box sx={{ marginBottom: "0%" }}>
        <Notes />
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 3,
            mb: 4,
            borderRadius: "10px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                }}
              >
                All Affordable Car Models
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {totalItems} {totalItems === 1 ? "result" : "results"} found
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewMode}
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiToggleButton-root": {
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  color: "black",
                  "&.Mui-selected": {
                    backgroundColor: "#28a745",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#218838",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list">
                <ListIcon fontSize={isMobile ? "small" : "medium"} />
              </ToggleButton>
              <ToggleButton value="grid">
                <GridView fontSize={isMobile ? "small" : "medium"} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {cars.length === 0 ? (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", margin: "40px 0" }}
          >
            No affordable cars found
          </Typography>
        ) : viewMode === "list" ? (
          <CarListingTable
            cars={cars}
            onCarClick={handleCarClick}
            imageUrl={imageUrl}
            onInquiryClick={handleInquiryClick}
          />
        ) : (
          <Grid container spacing={isMobile ? 1 : 3}>
            {cars.map((car) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={car.carModelId}>
                <CarListingCardGrid
                  car={car}
                  onCarClick={handleCarClick}
                  imageUrl={
                    imageUrl[car.carModelId] || "carousel/carslider.png"
                  }
                  onInquiryClick={handleInquiryClick}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {cars.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              mb: 4,
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "black",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Results per page: {itemsPerPage}
              </Typography>
            </Box>

            <Pagination
              count={totalPages || 0}
              page={currentPage}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  borderColor: "black",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "rgba(40, 167, 69, 0.1)",
                    borderColor: "#28a745",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "#28a745 !important",
                  color: "white",
                  borderColor: "#28a745",
                  "&:hover": {
                    backgroundColor: "#218838 !important",
                    borderColor: "#1e7e34",
                  },
                },
              }}
            />

            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              variant="outlined"
              displayEmpty
              size="small"
              sx={{
                width: 80,
                color: "black",
                fontWeight: 500,
                backgroundColor: "#fff",
                borderColor: "black",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "black",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#28a745",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#28a745",
                },
                "& .MuiSvgIcon-root": {
                  color: "#28a745",
                },
              }}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
        )}
      </Container>

           <InquiryModal
        open={openInquiryModal}
        onClose={handleInquiryClose}
        product={inquiryProduct}
        imageUrl1={modalImage}
      />

      <Box sx={{ width: '99.8vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw' }}>
        <FooterManual />
      </Box>
    </Container>
  
  );
};

export default SeeAllAffordableCarModels;